import React, { useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import { useTable, useSortBy, Column, TableOptions, UseSortByColumnProps } from 'react-table';
import { fetchCaseAuditLog } from '../../api/cases';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { formatTimestamp } from '../../utils/dateFormatters';

interface AuditLogEntry {
  timestamp: string;
  user: string;
  action: string;
  details: string;
}

const AuditLogTable: React.FC<{ refreshTrigger?: number }> = ({ refreshTrigger = 0 }) => {
  const { case_id } = useParams<{ case_id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const auth = useAuth();

  useEffect(() => {
    const loadAuditLog = async () => {
      try {
        const response = await fetchCaseAuditLog(case_id!, auth.user?.id_token);
        setAuditLogs(response.data.audit_logs);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (auth.user?.id_token) {
      loadAuditLog();
    }
  }, [case_id, auth.user?.id_token, refreshTrigger]);

  const columns: Column<AuditLogEntry>[] = React.useMemo(
    () => [
      {
        Header: 'Date/Time',
        accessor: 'timestamp',
        Cell: ({ value }) => formatTimestamp(value)
      },
      {
        Header: 'User',
        accessor: 'user',
      },
      {
        Header: 'Action',
        accessor: 'action',
      },
      {
        Header: 'Details',
        accessor: 'details',
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable<AuditLogEntry>(
    { 
      columns, 
      data: auditLogs,
      initialState: {
        sortBy: [
          {
            id: 'timestamp',
            desc: true
          }
        ]
      },
      disableSortRemove: true,
      disableMultiSort: true
    } as TableOptions<AuditLogEntry>,
    useSortBy
  );

  if (loading) {
    return <div>Loading audit log...</div>;
  }

  if (error) {
    return <div className="text-danger">Error loading audit log: {error}</div>;
  }

  return (
    <Table {...getTableProps()} striped bordered hover responsive>
      <thead className="table-dark">
        {headerGroups.map(headerGroup => {
          const { key: groupKey, ...groupProps } = headerGroup.getHeaderGroupProps();
          return (
            <tr key={groupKey} {...groupProps}>
              {headerGroup.headers.map(column => {
                const headerProps = column.getHeaderProps((column as unknown as UseSortByColumnProps<AuditLogEntry>).getSortByToggleProps());
                const { key, ...otherHeaderProps } = headerProps;
                return (
                  <th key={key} {...otherHeaderProps}>
                    {column.render('Header')}
                    <span>
                      {(column as unknown as UseSortByColumnProps<AuditLogEntry>).isSorted
                        ? (column as unknown as UseSortByColumnProps<AuditLogEntry>).isSortedDesc
                          ? ' ▽'
                          : ' △'
                        : ''}
                    </span>
                  </th>
                );
              })}
            </tr>
          );
        })}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map(row => {
          prepareRow(row);
          const { key: rowKey, ...rowProps } = row.getRowProps();
          return (
            <tr key={rowKey} {...rowProps}>
              {row.cells.map(cell => {
                const { key: cellKey, ...cellProps } = cell.getCellProps();
                return (
                  <td key={cellKey} {...cellProps}>{cell.render('Cell')}</td>
                );
              })}
            </tr>
          );
        })}
        {rows.length === 0 && (
          <tr>
            <td colSpan={4} className="text-center">No audit log entries available</td>
          </tr>
        )}
      </tbody>
    </Table>
  );
};

export default AuditLogTable;